"use client";

import {
  Button,
  Drawer,
  NumberInput,
  Separator,
  useToast,
} from "@boostdev/design-system-components/client";
import Image from "next/image";
import { TrashIcon } from "@/components/atoms/icons/TrashIcon";
import { useClientTranslation } from "@/lib/i18n/client";
import { formatPrice } from "@/lib/shop/money";
import { updateCartQty, removeFromCart } from "@/lib/shop/cart/actions";
import { storageImageSrc } from "@/lib/shop/images";
import type { ResolvedCart, ResolvedCartLine } from "@/lib/shop/cart/resolve";
import css from "./CartDrawer.module.css";

// ── Image helpers ──────────────────────────────────────────────────────────────

function resolveThumbSrc(line: ResolvedCartLine): string | null {
  const { image, variant } = line;
  if (image?.immichAssetId) return `/api/immich/thumb/${image.immichAssetId}`;
  if (image?.storagePath) return storageImageSrc(image.storagePath);
  if (variant.immichAssetId) return `/api/immich/thumb/${variant.immichAssetId}`;
  return null;
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface CartDrawerProps {
  locale: string;
  isOpen: boolean;
  onClose: () => void;
  cart: ResolvedCart;
  onCartChange: () => Promise<void>;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function CartDrawer({
  locale,
  isOpen,
  onClose,
  cart,
  onCartChange,
}: CartDrawerProps): React.ReactElement {
  const { t } = useClientTranslation(locale, "shop");
  const { showToast } = useToast();

  const handleQtyChange = async (variantId: string, qty: number): Promise<void> => {
    try {
      await updateCartQty(variantId, qty);
      await onCartChange();
    } catch {
      showToast(t("cart.updateError"), "error");
    }
  };

  const handleRemove = async (variantId: string): Promise<void> => {
    try {
      await removeFromCart(variantId);
      await onCartChange();
    } catch {
      showToast(t("cart.removeError"), "error");
    }
  };

  const isEmpty = cart.lines.length === 0;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={t("cart.title")}
      side="right"
    >
      <div className={css.body}>
        {cart.droppedVariantIds.length > 0 && (
          <p className={css.unavailableNotice} role="alert">
            {t("cart.unavailable")}
          </p>
        )}

        {isEmpty ? (
          <p className={css.emptyState}>{t("cart.empty")}</p>
        ) : (
          <ul className={css.lines} aria-label={t("cart.title")}>
            {cart.lines.map((line) => {
              const thumbSrc = resolveThumbSrc(line);
              return (
                <li key={line.variant.id} className={css.line}>
                  {thumbSrc ? (
                    <Image
                      src={thumbSrc}
                      alt={line.image?.alt ?? line.product.title}
                      className={css.thumb}
                      width={64}
                      height={64}
                    />
                  ) : (
                    <div className={css.thumbPlaceholder} aria-hidden="true" />
                  )}

                  <div className={css.lineDetails}>
                    <span className={css.lineTitle}>{line.product.title}</span>
                    <span className={css.lineVariant}>{line.variant.title}</span>
                    <span className={css.linePrice}>
                      {formatPrice(line.lineTotalCents, locale)}
                    </span>

                    <div className={css.lineActions}>
                      <div className={css.qty}>
                        <NumberInput
                          label={t("product.quantity")}
                          name={`qty-${line.variant.id}`}
                          value={line.qty}
                          min={1}
                          onChange={(qty) => {
                            void handleQtyChange(line.variant.id, qty);
                          }}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="small"
                        isIconOnly
                        aria-label={t("cart.remove")}
                        onClick={() => {
                          void handleRemove(line.variant.id);
                        }}
                      >
                        <TrashIcon aria-hidden />
                      </Button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <Separator />

        <div className={css.footer}>
          <div className={css.totals}>
            <span>{t("cart.subtotal")}</span>
            <span>{formatPrice(cart.subtotalCents, locale)}</span>
          </div>

          <Button
            href={`/${locale}/shop/checkout`}
            disabled={isEmpty}
          >
            {t("cart.checkout")}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
